import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Award, Package, Upload, Wand2, CheckCircle, Truck, Star, Instagram, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Layout from '../components/layout/Layout';
import { BRAND, IMAGES, USP_CARDS, HOW_IT_WORKS_STEPS, COLLECTIONS, REVIEWS } from '../data/mock';
import TrustBadges from '../components/cro/TrustBadges';
import BeforeAfterSection from '../components/cro/BeforeAfterSection';
import SocialProof from '../components/cro/SocialProof';
import { trackCTAClick, trackWhatsAppClick, trackInstagramClick } from '../lib/analytics';
import HomepageProductSlide from '../components/shop/HomepageProductSlide';
import { getProducts } from '../data/products';

const WHATSAPP = '918889616972';
const INSTAGRAM_USER = 'aura_printiqo';

const iconMap = {
  Sparkles, Award, Package, Upload, Wand2, CheckCircle, Truck
};

const handleWhatsApp = (location = 'hero') => {
  trackWhatsAppClick(location);
  const message = `Hi AuraPrintiqo! 👋\n\nI want to create a custom anime-style print.\n\nCould you please guide me on how to get started? I'll share my photo and requirements.`;
  window.open(`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
};

const handleInstagram = (location = 'hero') => {
  trackInstagramClick(location);
  window.open(`https://ig.me/m/${INSTAGRAM_USER}`, '_blank', 'noopener,noreferrer');
};

const HomePage = () => {
  const [shopProducts, setShopProducts] = React.useState([]);
  React.useEffect(() => { setShopProducts(getProducts()); }, []);

  return (
    <Layout>

      {/* ── Hero Section ── */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-gradient-to-br from-[hsl(var(--cream))] via-white to-[hsl(var(--peach))]">
        {/* Decorative blobs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-[hsl(350,70%,65%)]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[hsl(20,90%,70%)]/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[hsl(var(--mint))]/20 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* Left Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 rounded-full shadow-soft mb-6 animate-fade-in-up">
                <Sparkles className="w-4 h-4 text-[hsl(350,70%,65%)]" />
                <span className="text-sm font-medium text-gray-700">AI-Powered Custom Prints</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6 animate-fade-in-up stagger-1">
                {BRAND.tagline}
              </h1>

              <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-xl mx-auto lg:mx-0 animate-fade-in-up stagger-2">
                {BRAND.subtagline}
              </p>

              {/* ── PRIMARY CTA BUTTONS — WhatsApp & Instagram ── */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up stagger-3 mb-4">
                <Button
                  size="lg"
                  onClick={handleWhatsApp}
                  className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white shadow-soft-lg hover:shadow-xl transition-all duration-300 text-base px-8"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Order via WhatsApp
                </Button>
                <Button
                  size="lg"
                  onClick={handleInstagram}
                  className="w-full sm:w-auto bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90 text-white shadow-soft-lg transition-all duration-300 text-base px-8"
                >
                  <Instagram className="w-5 h-5 mr-2" />
                  Order via Instagram
                </Button>
              </div>

              {/* Secondary links */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start animate-fade-in-up stagger-3 mb-10">
                <Link to="/contact">
                  <Button size="sm" variant="outline" className="w-full sm:w-auto border border-gray-200 hover:border-[hsl(350,70%,65%)] hover:text-[hsl(350,70%,55%)] transition-all text-sm px-6">
                    Fill Order Form
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
                <Link to="/collections">
                  <Button size="sm" variant="ghost" className="w-full sm:w-auto text-gray-500 hover:text-gray-900 text-sm px-6">
                    View Collections
                  </Button>
                </Link>
              </div>

              {/* Social Proof */}
              <div className="flex items-center gap-4 justify-center lg:justify-start animate-fade-in-up stagger-4">
                <div className="flex -space-x-3">
                  {['PS', 'RM', 'SK', 'AP'].map((initials, i) => (
                    <div
                      key={i}
                      className="w-10 h-10 rounded-full bg-gradient-to-br from-[hsl(350,70%,75%)] to-[hsl(20,90%,80%)] border-2 border-white flex items-center justify-center text-xs font-semibold text-white shadow-soft"
                    >
                      {initials}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map((star) => (
                      <Star key={star} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-sm text-gray-600">100+ Happy Customers</p>
                </div>
              </div>
            </div>

            {/* Right — Hero Image */}
            <div className="relative animate-fade-in-up stagger-2">
              <div className="relative z-10">
                <div className="relative rounded-3xl overflow-hidden shadow-soft-lg">
                  <img
                    src={IMAGES.hero}
                    alt="Custom anime print example"
                    className="w-full h-[400px] sm:h-[500px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                </div>

                {/* Floating badge — bottom left */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-soft-lg animate-float">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[hsl(var(--mint))] flex items-center justify-center">
                      <Wand2 className="w-6 h-6 text-[hsl(var(--mint-dark))]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">AI Artwork</p>
                      <p className="text-xs text-gray-500">Ready in 24hrs</p>
                    </div>
                  </div>
                </div>

                {/* Floating badge — top right */}
                <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-4 shadow-soft-lg animate-float" style={{ animationDelay: '1s' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[hsl(var(--coral-light))] flex items-center justify-center">
                      <Award className="w-6 h-6 text-[hsl(var(--coral))]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Premium Quality</p>
                      <p className="text-xs text-gray-500">180 GSM Cotton</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Trust Badges Strip ── */}
      <TrustBadges />

      {/* ── Featured Product Slide ── */}
      <HomepageProductSlide products={shopProducts} />

      {/* ── Before / After Transformation ── */}
      <BeforeAfterSection
        beforeImage={IMAGES.petPhoto}
        afterImage={IMAGES.petAnimeArt}
        finalImage={IMAGES.petTshirt}
        title="See the AI Transformation"
        subtitle="Send us your photo — we turn it into anime artwork and print it on premium apparel"
      />

      {/* ── USP Cards ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {USP_CARDS.map((card, index) => {
              const IconComponent = iconMap[card.icon];
              return (
                <Card key={card.id} className="border-0 shadow-soft hover:shadow-soft-lg transition-all duration-300 hover-lift bg-gradient-to-br from-white to-[hsl(var(--cream))]">
                  <CardContent className="p-8">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${
                      index === 0 ? 'bg-[hsl(var(--lavender))]' :
                      index === 1 ? 'bg-[hsl(var(--mint))]' :
                      'bg-[hsl(var(--coral-light))]'
                    }`}>
                      <IconComponent className={`w-7 h-7 ${
                        index === 0 ? 'text-[hsl(var(--lavender-dark))]' :
                        index === 1 ? 'text-[hsl(var(--mint-dark))]' :
                        'text-[hsl(var(--coral))]'
                      }`} />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{card.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{card.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="py-20 bg-gradient-to-b from-[hsl(var(--cream))] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-[hsl(350,70%,65%)]/10 rounded-full text-sm font-medium text-[hsl(350,70%,55%)] mb-4">
              Simple Process
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Getting your custom anime print is easy. Just follow these simple steps!
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {HOW_IT_WORKS_STEPS.map((step, index) => {
              const IconComponent = iconMap[step.icon];
              return (
                <div key={step.id} className="text-center relative">
                  {index < HOW_IT_WORKS_STEPS.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-[calc(50%+2rem)] right-0 h-0.5 bg-gradient-to-r from-[hsl(350,70%,65%)]/30 to-[hsl(20,90%,70%)]/30" />
                  )}
                  <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[hsl(350,70%,65%)]/10 to-[hsl(20,90%,70%)]/10 mb-6 shadow-soft">
                    <IconComponent className="w-9 h-9 text-[hsl(350,70%,60%)]" />
                    <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-gradient-to-br from-[hsl(350,70%,65%)] to-[hsl(20,90%,70%)] text-white text-xs font-bold flex items-center justify-center shadow-soft">
                      {step.id}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/how-it-works">
              <Button variant="outline" className="border-2 border-gray-200 hover:border-[hsl(350,70%,65%)] hover:text-[hsl(350,70%,55%)] transition-all">
                Learn More
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Collections ── */}
      <section id="collections-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-[hsl(var(--lavender))]/50 rounded-full text-sm font-medium text-[hsl(var(--lavender-dark))] mb-4">
              Our Collections
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Choose Your Style</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From pet portraits to couple prints, we have something for everyone
            </p>
          </div>

          {/* Desktop grid */}
          <div className="hidden lg:grid lg:grid-cols-4 gap-6">
            {COLLECTIONS.map((collection) => (
              <Link key={collection.id} to={`/collections/${collection.id}`} className="group">
                <div className="relative rounded-2xl overflow-hidden shadow-soft hover:shadow-soft-lg transition-all duration-300 hover-lift">
                  <img
                    src={collection.image}
                    alt={collection.title}
                    className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="text-lg font-bold text-white mb-1">{collection.title}</h3>
                    <p className="text-gray-300 text-xs mb-2 line-clamp-2">{collection.shortDescription}</p>
                    <span className="text-xs font-semibold text-[hsl(20,90%,75%)]">{collection.priceRange}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Mobile scroll */}
          <div className="lg:hidden flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory">
            {COLLECTIONS.map((collection) => (
              <Link key={collection.id} to={`/collections/${collection.id}`} className="group flex-shrink-0 w-72 snap-start">
                <div className="relative rounded-2xl overflow-hidden shadow-soft">
                  <img
                    src={collection.image}
                    alt={collection.title}
                    className="w-full h-56 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-base font-bold text-white mb-1">{collection.title}</h3>
                    <span className="text-xs font-semibold text-[hsl(20,90%,75%)]">{collection.priceRange}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/shop">
              <Button size="lg" className="bg-gradient-to-r from-[hsl(350,70%,65%)] to-[hsl(20,90%,70%)] hover:from-[hsl(350,70%,60%)] hover:to-[hsl(20,90%,65%)] text-white px-10">
                Browse All Products
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/collections">
              <Button size="lg" variant="outline" className="border-2 border-gray-200 hover:border-[hsl(350,70%,65%)] hover:text-[hsl(350,70%,55%)] px-10">
                View Collections
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Reviews ── */}
      <SocialProof />

      {/* ── Final CTA ── */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[hsl(350,70%,50%)] rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[hsl(20,90%,50%)] rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Create Your
            <span className="block mt-2 bg-gradient-to-r from-[hsl(350,70%,70%)] to-[hsl(20,90%,75%)] bg-clip-text text-transparent">
              Custom Anime Print?
            </span>
          </h2>
          <p className="text-lg text-gray-300 mb-10 max-w-2xl mx-auto">
            Get started today! Send us your photo and we'll transform it into beautiful anime-style artwork.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => handleWhatsApp('final_cta')}
              className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white px-8 shadow-lg text-base"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Order via WhatsApp
            </Button>
            <Button
              size="lg"
              onClick={() => handleInstagram('final_cta')}
              className="w-full sm:w-auto bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90 text-white px-8 shadow-lg text-base"
            >
              <Instagram className="w-5 h-5 mr-2" />
              Order via Instagram
            </Button>
          </div>
        </div>
      </section>

    </Layout>
  );
};

export default HomePage;
